/**
 * CANOPY — the self-distilling knowledge tree (canopy README §B5, CNPY-7/8).
 *
 * `canopy.*` answers BARE JSON (like `wisdom.*`). Every node arrives with its labels, because a reader
 * that cannot tell a current summary from a stale one, or a person's words from a model's, is
 * navigating blind: `stale` (+ since when, + why), `willNotDistill` (a file too large or binary to
 * summarise — it will stay unsummarised), `source` (`human | model | mixed`), and the model that
 * distilled it. The human preamble and the model distillate arrive in SEPARATE fields, exactly as
 * the server stores them in separate columns.
 */
import { z } from "zod";

const nullableString = z.string().nullable().default(null);

export const CanopyScopeSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  enabled: z.boolean().default(true),
  nodes: z.number().default(0),
  stale: z.number().default(0),
  willNotDistill: z.number().default(0),
  rootId: nullableString,
});
export type CanopyScope = z.infer<typeof CanopyScopeSchema>;

export const CanopyNodeFactsSchema = z.object({
  id: z.string(),
  path: z.string(),
  title: z.string(),
  kind: z.string(),
  depth: z.number().default(0),
  refKind: nullableString,
  refId: nullableString,
  childCount: z.number().default(0),
  stale: z.boolean().default(true),
  staleSince: nullableString,
  staleReason: nullableString,
  willNotDistill: z.boolean().default(false),
  source: nullableString,
  distilledAt: nullableString,
  distillerThinker: nullableString,
  distillerModel: nullableString,
});
export type CanopyNodeFacts = z.infer<typeof CanopyNodeFactsSchema>;

export const CanopyNodeViewSchema = z.object({
  scope: CanopyScopeSchema,
  node: CanopyNodeFactsSchema,
  humanPreamble: nullableString,
  distillate: nullableString,
  ancestors: z
    .array(z.object({ id: z.string(), path: z.string(), title: z.string(), kind: z.string() }))
    .default([]),
  children: z.array(z.object({ node: CanopyNodeFactsSchema, gist: nullableString })).default([]),
  childTotal: z.number().default(0),
  childOffset: z.number().default(0),
  nextOffset: z.number().nullable().default(null),
  withheldChildren: z.number().default(0),
  budget: z
    .object({ tokens: z.number(), used: z.number(), truncated: z.boolean() })
    .default({ tokens: 0, used: 0, truncated: false }),
  boosted: z.boolean().default(false),
});
export type CanopyNodeView = z.infer<typeof CanopyNodeViewSchema>;

export const CanopyFindResultSchema = z.object({
  query: z.string(),
  hits: z
    .array(
      z.object({
        node: CanopyNodeFactsSchema,
        scope: z.string().default(""),
        ancestorTitles: z.array(z.string()).default([]),
        gist: nullableString,
      }),
    )
    .default([]),
  withheld: z.number().default(0),
  degraded: z.boolean().default(false),
  skippedReason: nullableString,
});
export type CanopyFindResult = z.infer<typeof CanopyFindResultSchema>;

export const CanopyOpenResultSchema = z.object({
  node: CanopyNodeFactsSchema,
  available: z.boolean(),
  reason: nullableString,
  text: nullableString,
  totalChars: z.number().default(0),
  capped: z.boolean().default(false),
  maxTokens: z.number().default(0),
});
export type CanopyOpenResult = z.infer<typeof CanopyOpenResultSchema>;

export const CanopyPerspectiveResultSchema = z.object({
  role: z.string(),
  kind: z.string(),
  description: nullableString,
  thinkerId: nullableString,
  model: nullableString,
  status: z.string(),
  error: nullableString,
  requestId: nullableString,
  inputDigest: z.string(),
  sourceRevision: z.string(),
  partial: z.boolean(),
  stale: z.boolean(),
  advice: z.array(
    z.object({
      intentId: z.string(),
      summary: z.string(),
      recommendation: z.string(),
      tradeoff: z.string(),
      unknowns: z.string(),
      sourceNodeIds: z.array(z.string()),
    }),
  ),
});
export type CanopyPerspectiveResult = z.infer<typeof CanopyPerspectiveResultSchema>;
export type CanopyPerspectiveRequest = {
  nodeId: string;
  projectKey: string;
  role: string;
  area?: string;
  intents: Array<{ id: string; question: string }>;
};

/** `canopy.tree.node`: by id, or by scope (id or slug) + path. */
export interface CanopyNodeQuery {
  id?: string;
  scope?: string;
  path?: string;
  offset?: number;
  budgetTokens?: number;
}

export interface CanopyApi {
  scopes(): Promise<CanopyScope[]>;
  /** Null when the node does not exist or the caller may not see it — the server answers both alike. */
  node(query: CanopyNodeQuery): Promise<CanopyNodeView | null>;
  find(input: { query: string; scope?: string; k?: number }): Promise<CanopyFindResult>;
  /** A file's or document's raw words, capped. Null when not found. */
  open(input: { id: string; maxTokens?: number }): Promise<CanopyOpenResult | null>;
  /** One configured project role's advisory reading, with each intent kept separate. */
  perspective(input: CanopyPerspectiveRequest): Promise<CanopyPerspectiveResult | null>;
}

/**
 * Why a node is stale, in words a person can act on. Unknown codes fall through to the code
 * itself rather than to a confident-sounding guess.
 */
export function staleReasonText(reason: string | null | undefined): string {
  switch (reason) {
    case null:
    case undefined:
    case "never_distilled":
      return "not summarised yet";
    case "content_changed":
      return "changed since it was summarised";
    case "moved_mid_distill":
      return "changed while it was being summarised";
    case "redistill_requested":
      return "queued to be summarised again";
    case "no_thinker":
    case "no_top_thinker":
      return "no model is set up for this level";
    case "prompt_over_budget":
    case "leaf_too_large":
      return "too large to summarise";
    case "leaf_binary":
      return "binary file — nothing to summarise";
    case "leaf_content_unavailable":
      return "the source could not be read";
    case "leaf_content_moved":
      return "the source moved — waiting for the next sync";
    case "thinker_error":
      return "the model failed — it will retry";
    case "empty_distillate":
    case "distillate_over_budget":
      return "the model's answer was unusable — it will retry";
    case "child_distillate_missing":
      return "waiting for its parts";
    case "distill_exhausted":
      return "gave up after repeated failures";
    default:
      return reason;
  }
}

// ── knowledge debugging (desk /knowledge/debug) ──────────────────────────────────────────────────

const nullableNumber = z.number().nullable().default(null);

export const DebugConnectionSchema = z.object({
  id: z.string(),
  kind: z.string(),
  remote: z.string().default(""),
  pathPrefix: nullableString,
  status: z.string().default(""),
  lastSnapshotAt: nullableString,
  lastSnapshotError: nullableString,
  snapshotSha: nullableString,
  rows: z.number().default(0),
  graphWorthy: z.number().default(0),
  graphFiles: nullableNumber,
  graphEmbedded: nullableNumber,
});
export type DebugConnection = z.infer<typeof DebugConnectionSchema>;

export const KnowledgeDebugReportSchema = z.object({
  scope: CanopyScopeSchema.nullable().default(null),
  scopes: z.array(CanopyScopeSchema).default([]),
  status: z
    .object({
      nodes: z.number(),
      fresh: z.number(),
      stale: z.number(),
      exhausted: z.number(),
      complete: z.boolean(),
      byReason: z.record(z.string(), z.number()).default({}),
      oldestStaleSince: nullableString,
      byDepth: z
        .array(z.object({ depth: z.number(), nodes: z.number(), stale: z.number() }))
        .default([]),
      driftedChildren: z.number().default(0),
      skippedSources: z
        .array(z.object({ kind: z.string(), ref: z.string(), reason: z.string() }))
        .default([]),
    })
    .nullable()
    .default(null),
  distiller: z.object({
    lastHour: z.number().default(0),
    lastDay: z.number().default(0),
    lastAt: nullableString,
    remaining: z.number().default(0),
    etaHours: nullableNumber,
  }),
  connections: z.array(DebugConnectionSchema).default([]),
  projection: z.object({
    graphNodes: nullableNumber,
    graphStale: nullableNumber,
    graphEmbedded: nullableNumber,
    projectedThrough: nullableString,
  }),
  graph: z
    .array(z.object({ label: z.string(), nodes: z.number(), embedded: z.number() }))
    .default([]),
  recent: z
    .array(
      z.object({
        id: z.string(),
        path: z.string(),
        title: z.string(),
        kind: z.string(),
        at: nullableString,
        model: nullableString,
        tokensIn: z.number().default(0),
        tokensOut: z.number().default(0),
      }),
    )
    .default([]),
  failures: z
    .array(
      z.object({
        id: z.string(),
        path: z.string(),
        title: z.string(),
        kind: z.string(),
        reason: nullableString,
        attempts: z.number().default(0),
        nextAttemptAt: nullableString,
        willNotDistill: z.boolean().default(false),
      }),
    )
    .default([]),
  graphAvailable: z.boolean().default(false),
  graphError: nullableString,
});
export type KnowledgeDebugReport = z.infer<typeof KnowledgeDebugReportSchema>;

export const DebugTreeCellSchema = z.object({
  id: z.string(),
  parentId: nullableString,
  path: z.string(),
  title: z.string(),
  kind: z.string(),
  depth: z.number(),
  leaves: z.number(),
  nodes: z.number(),
  current: z.number(),
  waiting: z.number(),
  willNotDistill: z.number(),
  stale: z.boolean().default(true),
  staleReason: nullableString,
});
export type DebugTreeCell = z.infer<typeof DebugTreeCellSchema>;

export const DebugTreeSchema = z.object({
  root: DebugTreeCellSchema,
  cells: z.array(DebugTreeCellSchema).default([]),
});
export type DebugTree = z.infer<typeof DebugTreeSchema>;

export interface KnowledgeDebugApi {
  report(scope?: string): Promise<KnowledgeDebugReport>;
  /** Null when the workspace has no tree for that scope. */
  tree(input: { scope?: string; nodeId?: string; depth?: number }): Promise<DebugTree | null>;
}
